import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  // Product-only OG fields. Match the server-side injection in
  // src/seo/inject.ts so HTML and Rendered stay in sync.
  productPrice?: number;
  productCurrency?: string;
  imageWidth?: number;
  imageHeight?: number;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = 'https://zerosmoke.in';
const DEFAULT_IMAGE = `${SITE_URL}/images/hero/og-cover.png`;

// ── DOM head manipulation helpers ──────────────────────────────
// Runs only client-side (callers guard with `typeof document`).

function setMetaByName(name: string, value: string | undefined) {
  const head = document.head;
  const selector = `meta[name="${name}"]`;
  const existing = head.querySelector<HTMLMetaElement>(selector);
  if (value === undefined || value === '') {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.setAttribute('content', value);
  } else {
    const m = document.createElement('meta');
    m.setAttribute('name', name);
    m.setAttribute('content', value);
    head.appendChild(m);
  }
}

function setMetaByProperty(property: string, value: string | undefined) {
  const head = document.head;
  const selector = `meta[property="${property}"]`;
  const existing = head.querySelector<HTMLMetaElement>(selector);
  if (value === undefined || value === '') {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.setAttribute('content', value);
  } else {
    const m = document.createElement('meta');
    m.setAttribute('property', property);
    m.setAttribute('content', value);
    head.appendChild(m);
  }
}

function setCanonical(href: string) {
  const head = document.head;
  const existing = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (existing) {
    existing.setAttribute('href', href);
  } else {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    l.setAttribute('href', href);
    head.appendChild(l);
  }
}

function absoluteImage(image: string | undefined): string {
  if (!image) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(image)) return image;
  return `${SITE_URL}${image.startsWith('/') ? image : '/' + image}`;
}

/**
 * Per-page SEO.
 *
 * Architecture: the SERVER (src/seo/inject.ts) is the single writer of
 * <head> meta on first paint — it injects per-route title, description,
 * canonical, OG, Twitter and JSON-LD via regex before sending HTML.
 *
 * On the client, this component renders no metadata as JSX (React 19's
 * native metadata hoisting would otherwise create duplicates with the
 * server-injected tags). Instead, a useEffect imperatively updates the
 * existing head tags whenever the props change — which is exactly when
 * the user navigates between routes via SPA.
 *
 * This keeps a single set of head meta tags at all times and ensures
 * SPA navigation updates everything a browser/extension/share sheet
 * might read: tab title, description, canonical, OG, Twitter cards.
 */
export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  productPrice,
  productCurrency,
  imageWidth,
  imageHeight,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const url = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    const image = absoluteImage(ogImage);

    // Document title (browser tab)
    document.title = title;

    // Standard meta
    setMetaByName('description', description);
    setCanonical(url);

    // Open Graph
    setMetaByProperty('og:title', title);
    setMetaByProperty('og:description', description);
    setMetaByProperty('og:url', url);
    setMetaByProperty('og:image', image);
    setMetaByProperty('og:image:secure_url', image);
    setMetaByProperty('og:type', ogType);
    setMetaByProperty('og:site_name', 'ZeroSmoke.in');
    setMetaByProperty('og:locale', 'en_IN');

    // Conditional OG product extensions
    setMetaByProperty(
      'og:image:width',
      imageWidth ? String(imageWidth) : undefined
    );
    setMetaByProperty(
      'og:image:height',
      imageHeight ? String(imageHeight) : undefined
    );
    setMetaByProperty(
      'og:price:amount',
      ogType === 'product' && productPrice != null
        ? productPrice.toFixed(2)
        : undefined
    );
    setMetaByProperty(
      'og:price:currency',
      ogType === 'product' && productPrice != null
        ? productCurrency ?? 'INR'
        : undefined
    );

    // Twitter card
    setMetaByName('twitter:card', 'summary_large_image');
    setMetaByName('twitter:title', title);
    setMetaByName('twitter:description', description);
    setMetaByName('twitter:image', image);
  }, [
    title,
    description,
    canonical,
    ogImage,
    ogType,
    productPrice,
    productCurrency,
    imageWidth,
    imageHeight,
  ]);

  // JSON-LD is rendered inline (script tags are fine anywhere in the
  // document — crawlers read structured data regardless of placement).
  // React owns this node so it updates naturally on route change.
  if (!jsonLd) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export { SITE_URL, DEFAULT_IMAGE };
